import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Card,
  Flex,
  Text,
  Grid,
  TextField,
  Badge,
  Button,
  Spinner,
} from "@radix-ui/themes";
import { ArrowLeftIcon } from "lucide-react";
import { integrationService } from "@/services/IntegrationService";
import { IntegrationDefinition } from "@/types/breeze/Integration";
import {
  siX,
  siClaude,
  siGooglecalendar,
  siGoogleanalytics,
  siGooglesearchconsole,
} from "simple-icons";

// Brand colors for integrations
const brandColors: Record<string, { bg: string; icon: string; text: string }> = {
  google: { bg: "#4285F4", icon: "white", text: "#1a73e8" },
  "google-calendar": { bg: "#4285F4", icon: "white", text: "#1a73e8" },
  "google-analytics": { bg: "#E37400", icon: "white", text: "#E37400" },
  "google-search-console": { bg: "#458CF5", icon: "white", text: "#1a73e8" },
  slack: { bg: "#4A154B", icon: "white", text: "#611f69" },
  github: { bg: "#24292e", icon: "white", text: "#24292e" },
  linkedin: { bg: "#0A66C2", icon: "white", text: "#0A66C2" },
  apple: { bg: "#000000", icon: "white", text: "#000000" },
  twitter: { bg: "#000000", icon: "white", text: "#000000" },
  claude: { bg: "#D97706", icon: "white", text: "#D97706" },
  openai: { bg: "#000000", icon: "white", text: "#000000" },
  grok: { bg: "#000000", icon: "white", text: "#000000" },
  gemini: { bg: "#4285F4", icon: "white", text: "#4285F4" },
};

const categoryColors: Record<string, string> = {
  Calendar: "blue",
  Analytics: "orange",
  Communication: "green",
  Authentication: "violet",
  Storage: "purple",
  Database: "orange",
  Marketing: "pink",
  CRM: "cyan",
  Social: "blue",
  AI: "amber",
  default: "gray",
};

// Helper to create React icon components from simple-icons
const fromSimpleIcon = (icon: { path: string }) => () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
    <path d={icon.path} />
  </svg>
);

// SVG icons for integrations — exported so StepNode can use them
const GoogleCalendarIcon = fromSimpleIcon(siGooglecalendar);
const GoogleAnalyticsIcon = fromSimpleIcon(siGoogleanalytics);
const GoogleSearchConsoleIcon = fromSimpleIcon(siGooglesearchconsole);

const SlackIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.124 2.521a2.528 2.528 0 0 1 2.52-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.52V8.834zm-1.271 0a2.528 2.528 0 0 1-2.521 2.521 2.528 2.528 0 0 1-2.521-2.521V2.522A2.528 2.528 0 0 1 15.166 0a2.528 2.528 0 0 1 2.521 2.522v6.312zm-2.521 10.124a2.528 2.528 0 0 1 2.521 2.52A2.528 2.528 0 0 1 15.166 24a2.528 2.528 0 0 1-2.521-2.522v-2.52h2.521zm0-1.271a2.528 2.528 0 0 1-2.521-2.521 2.528 2.528 0 0 1 2.521-2.521h6.312A2.528 2.528 0 0 1 24 15.166a2.528 2.528 0 0 1-2.522 2.521h-6.312z" />
  </svg>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const TwitterXIcon = fromSimpleIcon(siX);
const ClaudeIcon = fromSimpleIcon(siClaude);

const OpenAiIcon = () => (
  <svg viewBox="0 0 16 16" width="24" height="24" fill="white">
    <path d="M14.949 6.547a3.94 3.94 0 0 0-.348-3.273 4.11 4.11 0 0 0-4.4-1.934A4.1 4.1 0 0 0 8.423.2 4.15 4.15 0 0 0 6.305.086a4.1 4.1 0 0 0-1.891.948 4.04 4.04 0 0 0-1.158 1.753 4.1 4.1 0 0 0-1.563.679A4 4 0 0 0 .554 4.72a3.99 3.99 0 0 0 .502 4.731 3.94 3.94 0 0 0 .346 3.274 4.11 4.11 0 0 0 4.402 1.933c.382.425.852.764 1.377.995.526.231 1.095.35 1.67.346 1.78.002 3.358-1.132 3.901-2.804a4.1 4.1 0 0 0 1.563-.68 4 4 0 0 0 1.14-1.253 3.99 3.99 0 0 0-.506-4.716m-6.097 8.406a3.05 3.05 0 0 1-1.945-.694l.096-.054 3.23-1.838a.53.53 0 0 0 .265-.455v-4.49l1.366.778q.02.011.025.035v3.722c-.003 1.653-1.361 2.992-3.037 2.996m-6.53-2.75a2.95 2.95 0 0 1-.36-2.01l.095.057L5.29 12.09a.53.53 0 0 0 .527 0l3.949-2.246v1.555a.05.05 0 0 1-.022.041L6.473 13.3c-1.454.826-3.311.335-4.15-1.098m-.85-6.94A3.02 3.02 0 0 1 3.07 3.949v3.785a.51.51 0 0 0 .262.451l3.93 2.237-1.366.779a.05.05 0 0 1-.048 0L2.585 9.342a2.98 2.98 0 0 1-1.113-4.094zm11.216 2.571L8.747 5.576l1.362-.776a.05.05 0 0 1 .048 0l3.265 1.86a3 3 0 0 1 1.173 1.207 2.96 2.96 0 0 1-.27 3.2 3.05 3.05 0 0 1-1.36.997V8.279a.52.52 0 0 0-.276-.445m1.36-2.015-.097-.057-3.226-1.855a.53.53 0 0 0-.53 0L6.249 6.153V4.598a.04.04 0 0 1 .019-.04L9.533 2.7a3.07 3.07 0 0 1 3.257.139c.474.325.843.778 1.066 1.303.223.526.289 1.103.191 1.664zM5.503 8.575 4.139 7.8a.05.05 0 0 1-.026-.037V4.049c0-.57.166-1.127.476-1.607s.752-.864 1.275-1.105a3.08 3.08 0 0 1 3.234.41l-.096.054-3.23 1.838a.53.53 0 0 0-.265.455zm.742-1.577 1.758-1 1.762 1v2l-1.755 1-1.762-1z"/>
  </svg>
);

const GrokIcon = () => (
  <svg viewBox="0 0 50 50" width="24" height="24" fill="white">
    <path d="M 47.658203 2.0566406 C 47.415078 2.0662656 47.174734 2.1631563 46.990234 2.3476562 L 40.880859 8.4648438 C 40.833859 8.4968438 40.789047 8.5351719 40.748047 8.5761719 L 19.146484 30.304688 C 18.777484 30.675688 18.755656 31.266109 19.097656 31.662109 C 19.440656 32.058109 20.027219 32.1245 20.449219 31.8125 L 35.529297 20.667969 C 35.657297 20.572969 35.818125 20.540172 35.953125 20.576172 C 36.101125 20.617172 36.145156 20.725719 36.160156 20.761719 C 37.941156 25.057719 36.981844 29.949656 33.714844 33.222656 C 30.821844 36.118656 26.849547 37.186391 22.810547 36.150391 C 22.575547 36.090391 22.321516 36.119422 22.103516 36.232422 L 16.794922 38.984375 C 16.467922 39.153375 16.259906 39.489422 16.253906 39.857422 C 16.247906 40.225422 16.445625 40.566094 16.765625 40.746094 C 19.618625 42.341094 22.706953 43.119141 25.751953 43.119141 C 30.372953 43.119141 34.89475 41.326094 38.34375 37.871094 C 42.74375 33.467094 44.536297 27.227297 43.154297 21.154297 C 43.150297 21.114297 43.142812 21.073203 43.132812 21.033203 C 41.376812 13.469203 43.505828 10.537469 48.173828 4.1054688 L 48.507812 3.640625 C 48.813813 3.217625 48.741703 2.6309688 48.345703 2.2929688 C 48.147703 2.1249687 47.901328 2.0470156 47.658203 2.0566406 z M 25.996094 7.1367188 C 21.206724 7.0397839 16.477547 8.8765469 12.935547 12.419922 C 7.8695469 17.492922 6.2917656 25.345937 9.0097656 31.960938 C 10.693766 36.058937 7.9562031 38.935484 4.7832031 42.271484 C 3.6512031 43.460484 2.4804844 44.691719 1.5214844 46.011719 C 1.2224844 46.425719 1.2801563 46.998797 1.6601562 47.341797 C 1.8501562 47.513797 2.0900781 47.599609 2.3300781 47.599609 C 2.5680781 47.599609 2.8080469 47.515703 2.9980469 47.345703 L 17.335938 34.525391 C 17.482938 34.416391 17.599922 34.268797 17.669922 34.091797 C 17.822922 33.712797 17.728594 33.278094 17.433594 32.996094 C 14.748594 30.429094 14.157766 27.483844 14.134766 25.464844 C 14.099766 22.358844 15.354984 19.296453 17.583984 17.064453 C 20.080984 14.562453 23.836578 13.331469 27.392578 13.855469 C 27.595578 13.883469 27.810047 13.850906 27.998047 13.753906 L 33.689453 10.800781 C 34.024453 10.627781 34.232516 10.278391 34.228516 9.9003906 C 34.224516 9.5223906 34.006016 9.1796719 33.666016 9.0136719 C 33.269016 8.8206719 32.857031 8.6362812 32.457031 8.4882812 C 30.361094 7.6242188 28.17308 7.18078 25.996094 7.1367188 z"/>
  </svg>
);

const GeminiIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
    <path d="M11.04 19.32Q12 21.51 12 24q0-2.49.93-4.68.96-2.19 2.58-3.81t3.81-2.55Q21.51 12 24 12q-2.49 0-4.68-.93a12.3 12.3 0 0 1-3.81-2.58 12.3 12.3 0 0 1-2.58-3.81Q12 2.49 12 0q0 2.49-.96 4.68-.93 2.19-2.55 3.81a12.3 12.3 0 0 1-3.81 2.58Q2.49 12 0 12q2.49 0 4.68.96 2.19.93 3.81 2.55t2.55 3.81"/>
  </svg>
);

export const integrationIcons: Record<string, React.FC> = {
  google: GoogleIcon,
  "google-calendar": GoogleCalendarIcon,
  "google-analytics": GoogleAnalyticsIcon,
  "google-search-console": GoogleSearchConsoleIcon,
  slack: SlackIcon,
  github: GitHubIcon,
  linkedin: LinkedInIcon,
  apple: AppleIcon,
  twitter: TwitterXIcon,
  claude: ClaudeIcon,
  openai: OpenAiIcon,
  grok: GrokIcon,
  gemini: GeminiIcon,
};

export const integrationBrandColors = brandColors;

interface Props {
  tenantId: number;
  onSelect: (definition: IntegrationDefinition) => void;
  selectedProvider?: string;
  onBrowsingChange?: (isBrowsing: boolean) => void;
}

export default function IntegrationSelector({
  tenantId,
  onSelect,
  selectedProvider,
  onBrowsingChange,
}: Props) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [browsingState, setBrowsingState] = useState(!selectedProvider);

  const setBrowsing = (val: boolean) => {
    setBrowsingState(val);
    onBrowsingChange?.(val);
  };
  const browsing = browsingState;

  const isValidTenant =
    typeof tenantId === "number" && !isNaN(tenantId) && tenantId > 0;

  const { data: definitions = [], isLoading } = useQuery({
    queryKey: ["integration-definitions", tenantId],
    queryFn: () => integrationService.getDefinitions(),
    enabled: isValidTenant,
    retry: false,
  });

  const { data: connections = [] } = useQuery({
    queryKey: ["integration-connections", tenantId],
    queryFn: () => integrationService.getConnections(),
    enabled: isValidTenant,
    retry: false,
  });

  // Check OAuth status for all workflow definitions
  const { data: oauthStatuses = {} } = useQuery({
    queryKey: ["integration-oauth-statuses", tenantId],
    queryFn: async () => {
      const results: Record<string, any> = {};
      for (const def of definitions.filter((d) => d.operations?.length > 0)) {
        const provider = def.parent_provider || def.provider;
        if (!results[provider]) {
          try {
            results[provider] =
              await integrationService.getIntegrationOAuthSettings(provider);
          } catch {
            results[provider] = null;
          }
        }
      }
      return results;
    },
    enabled: definitions.length > 0 && isValidTenant,
  });

  if (isLoading) {
    return (
      <Flex justify="center" py="6">
        <Spinner size="3" />
      </Flex>
    );
  }

  // Only show integrations with operations in the workflow step selector (not social auth providers)
  const workflowDefinitions = definitions.filter(
    (d) => d.operations?.length > 0,
  );

  const categories = Array.from(
    new Set(workflowDefinitions.map((d) => d.category)),
  ).sort();

  const filtered = workflowDefinitions.filter((d) => {
    const matchesSearch =
      !search ||
      d.display_name.toLowerCase().includes(search.toLowerCase()) ||
      d.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !category || d.category === category;
    return matchesSearch && matchesCategory;
  });

  const isReady = (def: IntegrationDefinition) => {
    // Has a direct connection (e.g. Slack)
    if (
      connections.some(
        (c: any) =>
          c.integration_definition_id === def.id && c.is_enabled !== false,
      )
    )
      return true;
    // Has OAuth configured (directly or via parent)
    const oauthProvider = def.parent_provider || def.provider;
    return oauthStatuses[oauthProvider]?.has_client_id === true;
  };

  // Collapsed view — show selected integration compactly
  const selectedDef = definitions.find((d) => d.provider === selectedProvider);
  if (selectedProvider && selectedDef && !browsing) {
    const IconComponent = integrationIcons[selectedDef.provider];
    const brand = brandColors[selectedDef.provider];

    return (
      <Card style={{ border: "2px solid var(--accent-9)" }}>
        <Flex justify="between" align="center" p="3">
          <Flex gap="3" align="center">
            <Box
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: brand?.bg || "var(--gray-4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {IconComponent ? <IconComponent /> : null}
            </Box>
            <Box>
              <Text size="2" weight="bold">
                {selectedDef.display_name}
              </Text>
              <Text size="1" color="gray" style={{ display: "block" }}>
                {selectedDef.description}
              </Text>
            </Box>
          </Flex>
          <Button variant="soft" size="1" onClick={() => setBrowsing(true)}>
            <ArrowLeftIcon size={12} /> Change
          </Button>
        </Flex>
      </Card>
    );
  }

  // Full browsing view
  return (
    <Box>
      <TextField.Root
        placeholder="Search integrations..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="3"
        mb="3"
      />

      <Flex gap="2" mb="4" wrap="wrap">
        <Badge
          variant={category === null ? "solid" : "soft"}
          size="2"
          style={{ cursor: "pointer" }}
          onClick={() => setCategory(null)}
        >
          All
        </Badge>
        {categories.map((c) => (
          <Badge
            key={c}
            variant={category === c ? "solid" : "soft"}
            color={categoryColors[c] as any}
            size="2"
            style={{ cursor: "pointer" }}
            onClick={() => setCategory(c === category ? null : c)}
          >
            {c}
          </Badge>
        ))}
      </Flex>

      {filtered.length === 0 ? (
        <Text
          size="2"
          color="gray"
          align="center"
          style={{ paddingTop: "24px", paddingBottom: "24px" }}
        >
          No integrations found
        </Text>
      ) : (
        <Grid columns="2" gap="4">
          {filtered.map((def) => {
            const IconComponent = integrationIcons[def.provider];
            const isSel = selectedProvider === def.provider;
            const isConn = isReady(def);
            const brand = brandColors[def.provider];

            return (
              <Card
                key={def.id}
                onClick={() => {
                  onSelect(def);
                  setBrowsing(false);
                }}
                style={{
                  cursor: "pointer",
                  border: isSel
                    ? "2px solid var(--accent-9)"
                    : "1px solid var(--gray-6)",
                }}
              >
                <Box p="4">
                  <Flex gap="4" align="start">
                    <Box
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        backgroundColor: brand?.bg || "var(--gray-4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {IconComponent ? <IconComponent /> : null}
                    </Box>

                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        size="5"
                        weight="bold"
                        mb="1"
                        style={{ display: "block" }}
                      >
                        {(
                          def.display_name ||
                          def.provider ||
                          "Unknown"
                        ).toUpperCase()}
                      </Text>
                      <Text
                        size="2"
                        color="gray"
                        style={{ display: "block", marginBottom: 8 }}
                      >
                        {def.description}
                      </Text>
                      <Flex gap="2">
                        <Badge variant="soft" size="1">
                          {def.category}
                        </Badge>
                        <Badge
                          variant="soft"
                          color={isConn ? "green" : "gray"}
                          size="1"
                        >
                          {isConn ? "Ready" : "Setup Required"}
                        </Badge>
                      </Flex>
                    </Box>
                  </Flex>
                </Box>
              </Card>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
